import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  AvatarGroup,
  Snackbar,
  useTheme,
  alpha,
  Fade,
  Zoom,
} from "@mui/material";
import {
  CheckCircleOutline,
  CancelOutlined,
  RefreshOutlined,
  VisibilityOutlined,
  AutorenewOutlined,
  MoneyOffOutlined,
  ImageOutlined,
  PendingActions,
  Close,
  FilterList,
  DashboardCustomize,
  TaskOutlined,
  ReceiptOutlined,
  LocalShippingOutlined,
  MoreVert,
} from "@mui/icons-material";
import { BASE_URL } from "../../components/config/config";
import { useSelector } from "react-redux";

// Create teal theme
const tealTheme = {
  palette: {
    primary: {
      main: "#00897b", // teal-600 equivalent
      dark: "#00695c", // teal-700
      light: "#4db6ac", // teal-300
    },
    secondary: {
      main: "#26a69a", // teal-400
    },
    success: {
      main: "#4caf50",
    },
    error: {
      main: "#f44336",
    },
    warning: {
      main: "#ff9800",
    },
    info: {
      main: "#2196f3",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
  },
};

const PartRequest = () => {
  const theme = useTheme();
  const globalState = useSelector((state) => state.clientReducer);
  const accessToken = globalState?.userTokens?.accessToken;
  const [partRequests, setPartRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decision, setDecision] = useState("");
  const [description, setDescription] = useState("");
  const [filters, setFilters] = useState({
    part_request_status: "",
    ticket_obj_id: "",
    ticket_status: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Enhanced status options with more actions
  const statusOptions = [
    {
      value: "PENDING",
      label: "Pending",
      color: "warning",
      icon: <PendingActions />,
      description: "Awaiting client action",
    },
    {
      value: "APPROVED",
      label: "Approved",
      color: "success",
      icon: <CheckCircleOutline />,
      description: "Client approved the parts",
    },
    {
      value: "REJECTED",
      label: "Rejected",
      color: "error",
      icon: <CancelOutlined />,
      description: "Client rejected the parts",
    },
    {
      value: "REFUNDED",
      label: "Refunded",
      color: "info",
      icon: <MoneyOffOutlined />,
      description: "Amount has been refunded",
    },
    {
      value: "REPLACED",
      label: "Replaced",
      color: "primary",
      icon: <AutorenewOutlined />,
      description: "Parts have been replaced",
    },
  ];

  // Action buttons configuration
  const actionButtons = [
    {
      value: "APPROVED",
      label: "Approve",
      color: "success",
      icon: <CheckCircleOutline />,
      description: "Approve the part request",
    },
    {
      value: "REJECTED",
      label: "Reject",
      color: "error",
      icon: <CancelOutlined />,
      description: "Reject the part request",
    },
    {
      value: "REFUNDED",
      label: "Refund",
      color: "info",
      icon: <MoneyOffOutlined />,
      description: "Request refund for parts",
    },
    {
      value: "REPLACED",
      label: "Replace",
      color: "primary",
      icon: <LocalShippingOutlined />,
      description: "Request part replacement",
    },
  ];

  // Ticket status options
  const ticketStatusOptions = [
    { value: "ON_HOLD", label: "On Hold", color: "warning" },
    { value: "IN_PROGRESS", label: "In Progress", color: "info" },
    { value: "COMPLETED", label: "Completed", color: "success" },
    { value: "CANCELLED", label: "Cancelled", color: "error" },
  ];

  // Show error in snackbar
  const showError = (message) => {
    setError(message);
    setSnackbarOpen(true);
  };

  // API call to get part requests
  const fetchPartRequests = async () => {
    setLoading(true);
    setError("");
    try {
      // Build query params
      const queryParams = new URLSearchParams();

      if (filters.ticket_obj_id) {
        queryParams.append("ticket_obj_id", filters.ticket_obj_id);
      }
      if (filters.part_request_status) {
        queryParams.append("part_request_status", filters.part_request_status);
      }
      if (filters.ticket_status) {
        queryParams.append("ticket_status", filters.ticket_status);
      }

      queryParams.append("client_id", "CL-86");

      const response = await fetch(
        `${BASE_URL}client/get-part-approval-tickets?${queryParams}`
      );

      const result = await response.json();

      if (result.success === 1) {
        setPartRequests(result.data || []);
        setFilteredRequests(result.data || []);
      } else {
        showError(result.message || "Failed to fetch part requests");
      }
    } catch (err) {
      showError("Failed to fetch part requests. Please try again.");
      console.error("Error fetching part requests:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters locally
  const applyFilters = () => {
    let filtered = partRequests;

    if (filters.part_request_status) {
      if (filters.part_request_status === "PENDING") {
        filtered = filtered.filter(
          (request) => !request.clientActionDescription
        );
      } else {
        filtered = filtered.filter(
          (request) =>
            request.part_request_status === filters.part_request_status
        );
      }
    }

    if (filters.ticket_obj_id) {
      filtered = filtered.filter((request) =>
        request.ticket_id
          ?.toLowerCase()
          .includes(filters.ticket_obj_id.toLowerCase())
      );
    }

    if (filters.ticket_status) {
      filtered = filtered.filter(
        (request) => request.ticket_status === filters.ticket_status
      );
    }

    setFilteredRequests(filtered);
  };

  // API call for part approval action
  const handlePartApprovalAction = async () => {
    if (!selectedRequest || !decision || !description) {
      showError("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}client/part-approval-action`, {
        method: "POST",
        headers: {
          "x-access-token": accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ticket_obj_id: selectedRequest._id,
          decision: decision,
          client_action_description: description,
        }),
      });

      const result = await response.json();

      if (result.success === 1) {
        // Refresh the data to get updated status from server
        await fetchPartRequests();

        // Close dialog and reset form
        setActionDialogOpen(false);
        setSelectedRequest(null);
        setDecision("");
        setDescription("");
        setError("");
      } else {
        showError(result.message || "Failed to submit decision");
      }
    } catch (err) {
      showError("Failed to submit decision. Please try again.");
      console.error("Error submitting part approval:", err);
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setDecision(action);
    setActionDialogOpen(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Enhanced status chip function
  const getStatusChip = (request) => {
    // If there's no client action, it's pending
    if (!request.clientActionDescription) {
      return (
        <Chip
          icon={<PendingActions />}
          label="Pending Action"
          color="warning"
          variant="filled"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      );
    }

    // Use the part_request_status from API response
    const statusOption = statusOptions.find(
      (opt) => opt.value === request.part_request_status
    );

    if (statusOption) {
      return (
        <Chip
          icon={statusOption.icon}
          label={statusOption.label}
          color={statusOption.color}
          variant="filled"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      );
    }

    // Fallback based on client action description existence
    return (
      <Chip
        icon={<CheckCircleOutline />}
        label="Processed"
        color="info"
        variant="filled"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const getTicketStatusChip = (status) => {
    const statusOption = ticketStatusOptions.find(
      (opt) => opt.value === status
    );
    return (
      <Chip
        label={statusOption?.label || status}
        color={statusOption?.color || "default"}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getPartNames = (partDetails) => {
    return (
      partDetails?.map((part) => part.part_name).join(", ") || "No parts listed"
    );
  };

  // Get action button for a request
  const getActionButtons = (request) => {
    if (request.clientActionDescription) {
      return (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            className="text-blue-600"
            onClick={() => {
              setSelectedRequest(request);
              setActionDialogOpen(true);
            }}
            sx={{
              backgroundColor: alpha("#00897b", 0.1),
              color: "#00897b",
              "&:hover": {
                backgroundColor: alpha("#00897b", 0.2),
              },
            }}
          >
            <VisibilityOutlined />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Box className="flex gap-1 flex-wrap">
        {actionButtons.map((action) => (
          <Tooltip key={action.value} title={action.description}>
            <IconButton
              size="small"
              sx={{
                color:
                  action.color === "primary"
                    ? "#00897b"
                    : `${action.color}.main`,
                backgroundColor: alpha(
                  action.color === "primary"
                    ? "#00897b"
                    : theme.palette[action.color].main,
                  0.1
                ),
                "&:hover": {
                  backgroundColor: alpha(
                    action.color === "primary"
                      ? "#00897b"
                      : theme.palette[action.color].main,
                    0.2
                  ),
                },
                transition: "all 0.2s ease-in-out",
                transform: "scale(1)",
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
              onClick={() => openActionDialog(request, action.value)}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    fetchPartRequests();
  }, []);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, partRequests]);

  return (
    <Box className="p-6 max-w-7xl mx-auto bg-white">
      {/* Enhanced Header */}
      <Box className="mb-8">
        <Typography
          variant="h4"
          component="h3"
          className="font-bold text-gray-900 mb-2"
          sx={{
            background: `linear-gradient(45deg, #00897b, #26a69a)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Part Approval Requests
        </Typography>
        <Typography variant="h6" className="text-gray-600 font-light">
          Review and manage part replacement requests for your service requests
        </Typography>
      </Box>

      {/* Enhanced Stats Summary */}
      <Fade in timeout={800}>
        <Box className="mb-8 grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            {
              label: "Total Requests",
              value: partRequests.length,
              color: "primary",
              icon: <TaskOutlined />,
            },
            {
              label: "Pending Action",
              value: partRequests.filter((req) => !req.clientActionDescription)
                .length,
              color: "warning",
              icon: <PendingActions />,
            },
            {
              label: "Approved",
              value: partRequests.filter(
                (req) => req.part_request_status === "APPROVED"
              ).length,
              color: "success",
              icon: <CheckCircleOutline />,
            },
            {
              label: "Rejected",
              value: partRequests.filter(
                (req) => req.part_request_status === "REJECTED"
              ).length,
              color: "error",
              icon: <CancelOutlined />,
            },
            {
              label: "Processed",
              value: partRequests.filter((req) => req.clientActionDescription)
                .length,
              color: "primary",
              icon: <ReceiptOutlined />,
            },
          ].map((stat, index) => (
            <Zoom
              in
              key={stat.label}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Card
                className="p-4 text-center relative overflow-hidden border"
                sx={{
                  background: `linear-gradient(135deg, ${alpha(
                    stat.color === "primary"
                      ? "#00897b"
                      : theme.palette[stat.color].main,
                    0.1
                  )}, ${alpha(
                    stat.color === "primary"
                      ? "#00897b"
                      : theme.palette[stat.color].main,
                    0.05
                  )})`,
                  border: `1px solid ${alpha(
                    stat.color === "primary"
                      ? "#00897b"
                      : theme.palette[stat.color].main,
                    0.2
                  )}`,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 25px ${alpha(
                      stat.color === "primary"
                        ? "#00897b"
                        : theme.palette[stat.color].main,
                      0.15
                    )}`,
                  },
                }}
              >
                <Box
                  className="absolute top-2 right-2 opacity-20"
                  sx={{
                    color:
                      stat.color === "primary"
                        ? "#00897b"
                        : theme.palette[stat.color].main,
                  }}
                >
                  {stat.icon}
                </Box>
                <Typography
                  variant="h3"
                  className="font-bold mb-2"
                  sx={{
                    color:
                      stat.color === "primary"
                        ? "#00897b"
                        : `${stat.color}.main`,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  className="text-gray-600 font-medium"
                >
                  {stat.label}
                </Typography>
              </Card>
            </Zoom>
          ))}
        </Box>
      </Fade>

      {/* Enhanced Filters */}
      <Fade in timeout={600}>
        <Card
          className="mb-6 shadow-lg rounded-xl border"
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              "#ffffff",
              0.9
            )}, ${alpha("#ffffff", 0.95)})`,
            borderColor: alpha("#00897b", 0.2),
          }}
        >
          <Box
            className="p-4 border-b"
            sx={{ borderColor: alpha("#00897b", 0.1) }}
          >
            <Box className="flex items-center justify-between">
              <Box className="flex items-center gap-2">
                <FilterList sx={{ color: "#00897b" }} />
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-800"
                >
                  Filters & Search
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                sx={{
                  borderColor: "#00897b",
                  color: "#00897b",
                  "&:hover": {
                    borderColor: "#00695c",
                    backgroundColor: alpha("#00897b", 0.04),
                  },
                  borderRadius: 2,
                }}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </Box>
          </Box>

          {showFilters && (
            <Box className="p-6">
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Request Status"
                    value={filters.part_request_status}
                    onChange={(e) =>
                      handleFilterChange("part_request_status", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: "200px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <MenuItem value="">All Requests</MenuItem>
                    <MenuItem value="PENDING">Pending Action</MenuItem>
                    <MenuItem value="APPROVED">Approved</MenuItem>
                    <MenuItem value="REJECTED">Rejected</MenuItem>
                    <MenuItem value="REFUNDED">Refunded</MenuItem>
                    <MenuItem value="REPLACED">Replaced</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Request ID"
                    value={filters.ticket_obj_id}
                    onChange={(e) =>
                      handleFilterChange("ticket_obj_id", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    placeholder="Enter request ID"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Ticket Status"
                    value={filters.ticket_status}
                    onChange={(e) =>
                      handleFilterChange("ticket_status", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: "200px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    {ticketStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Box className="flex gap-3 mt-6">
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  startIcon={<RefreshOutlined />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    backgroundColor: "#00897b",
                    background: `linear-gradient(45deg, #00897b, #26a69a)`,
                    "&:hover": {
                      backgroundColor: "#00695c",
                      background: `linear-gradient(45deg, #00695c, #00897b)`,
                    },
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      part_request_status: "",
                      ticket_obj_id: "",
                      ticket_status: "",
                    });
                  }}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    borderColor: "#00897b",
                    color: "#00897b",
                    "&:hover": {
                      borderColor: "#00695c",
                      backgroundColor: alpha("#00897b", 0.04),
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      </Fade>

      {/* Enhanced Part Requests Table */}
      <Fade in timeout={800}>
        <Card
          className="shadow-lg rounded-xl overflow-hidden border"
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              "#ffffff",
              0.95
            )}, ${alpha("#ffffff", 0.98)})`,
            borderColor: alpha("#00897b", 0.1),
          }}
        >
          <TableContainer>
            <Table>
              <TableHead
                sx={{
                  background: `linear-gradient(45deg, #00897b, #26a69a)`,
                }}
              >
                <TableRow>
                  {[
                    "Request ID",
                    "Part Details",
                    "Parts Count",
                    "Total Price",
                    "Request Status",
                    "Approval Status",
                    "Images",
                    "Actions",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        py: 2,
                        borderBottom: "none",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <CircularProgress sx={{ color: "#00897b" }} size={40} />
                      <Typography className="mt-3 text-gray-600 font-medium">
                        Loading part requests...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Box className="text-gray-400 mb-2">
                        <TaskOutlined sx={{ fontSize: 48 }} />
                      </Box>
                      <Typography variant="h6" className="text-gray-500 mb-2">
                        No part requests found
                      </Typography>
                      <Typography variant="body2" className="text-gray-400">
                        Try adjusting your filters or search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request, index) => (
                    <TableRow
                      key={request._id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: alpha("#00897b", 0.02),
                        },
                        "&:hover": {
                          backgroundColor: alpha("#00897b", 0.04),
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          className="font-mono text-sm font-bold"
                          sx={{ color: "#00897b" }}
                        >
                          {request.ticket_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          className="font-semibold text-gray-800"
                        >
                          {getPartNames(request.part_details)}
                        </Typography>
                        {request.clientActionDescription && (
                          <Typography
                            variant="caption"
                            className="text-gray-500 block mt-1"
                          >
                            Response: {request.clientActionDescription}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.total_part_count}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderColor: "#00897b",
                            color: "#00897b",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          className="font-bold"
                          sx={{ color: "#00897b" }}
                        >
                          {formatCurrency(request.total_part_prize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getTicketStatusChip(request.ticket_status)}
                      </TableCell>
                      <TableCell>{getStatusChip(request)}</TableCell>
                      <TableCell>
                        {request.partPictures &&
                        request.partPictures.length > 0 ? (
                          <AvatarGroup max={3}>
                            {request.partPictures.map((pic, index) => (
                              <Tooltip key={index} title="View Image">
                                <Avatar
                                  src={pic}
                                  variant="rounded"
                                  sx={{
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    border: `1px solid ${alpha(
                                      "#00897b",
                                      0.2
                                    )}`,
                                    "&:hover": {
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                >
                                  <ImageOutlined />
                                </Avatar>
                              </Tooltip>
                            ))}
                          </AvatarGroup>
                        ) : (
                          <Tooltip title="No images available">
                            <IconButton size="small" disabled>
                              <ImageOutlined className="text-gray-400" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{getActionButtons(request)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Fade>

      {/* Enhanced Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              "#ffffff",
              0.98
            )}, ${alpha("#ffffff", 0.95)})`,
            border: `1px solid ${alpha("#00897b", 0.2)}`,
          },
        }}
      >
        <DialogTitle
          className="border-b"
          sx={{
            background: `linear-gradient(45deg, #00897b, #26a69a)`,
            color: "white",
          }}
        >
          <Typography variant="h6" className="font-bold">
            {selectedRequest?.clientActionDescription
              ? "Request Details"
              : `${
                  decision === "APPROVED"
                    ? "Approve"
                    : decision === "REJECTED"
                    ? "Reject"
                    : decision === "REFUNDED"
                    ? "Refund"
                    : "Replace"
                } Part Request`}
          </Typography>
        </DialogTitle>

        <DialogContent className="pt-6">
          {selectedRequest && (
            <Box className="space-y-4">
              {!selectedRequest.clientActionDescription ? (
                <>
                  <Typography variant="body2" className="text-gray-600">
                    You are about to <strong>{decision.toLowerCase()}</strong>{" "}
                    the part request for:
                  </Typography>

                  <Card
                    className="p-4 space-y-2"
                    variant="outlined"
                    sx={{
                      background: alpha("#00897b", 0.03),
                      borderColor: alpha("#00897b", 0.2),
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Request ID:</strong> {selectedRequest.ticket_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Parts:</strong>{" "}
                      {getPartNames(selectedRequest.part_details)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Parts:</strong>{" "}
                      {selectedRequest.total_part_count}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Part Price:</strong>{" "}
                      {formatCurrency(selectedRequest.total_part_prize)}
                    </Typography>
                  </Card>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description"
                    placeholder={`Please provide reason for ${decision.toLowerCase()}...`}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    variant="outlined"
                    required
                    helperText="This description will be recorded with your decision"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="body2" className="text-gray-600">
                    Request details for:
                  </Typography>

                  <Card
                    className="p-4 space-y-2"
                    variant="outlined"
                    sx={{
                      background: alpha("#00897b", 0.03),
                      borderColor: alpha("#00897b", 0.2),
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Request ID:</strong> {selectedRequest.ticket_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Parts:</strong>{" "}
                      {getPartNames(selectedRequest.part_details)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Parts:</strong>{" "}
                      {selectedRequest.total_part_count}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Part Price:</strong>{" "}
                      {formatCurrency(selectedRequest.total_part_prize)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong>{" "}
                      <Chip
                        label={selectedRequest.part_request_status}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2">
                      <strong>Your Response:</strong>{" "}
                      {selectedRequest.clientActionDescription}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Responded At:</strong>{" "}
                      {new Date(selectedRequest.respondedAt).toLocaleString()}
                    </Typography>
                  </Card>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions className="p-6 border-t">
          <Button
            onClick={() => setActionDialogOpen(false)}
            variant="outlined"
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: "#00897b",
              color: "#00897b",
              "&:hover": {
                borderColor: "#00695c",
                backgroundColor: alpha("#00897b", 0.04),
              },
            }}
          >
            {selectedRequest?.clientActionDescription ? "Close" : "Cancel"}
          </Button>
          {!selectedRequest?.clientActionDescription && (
            <Button
              onClick={handlePartApprovalAction}
              variant="contained"
              disabled={loading || !description.trim()}
              sx={{
                borderRadius: 2,
                px: 3,
                backgroundColor: "#00897b",
                background: `linear-gradient(45deg, #00897b, #26a69a)`,
                "&:hover": {
                  backgroundColor: "#00695c",
                  background: `linear-gradient(45deg, #00695c, #00897b)`,
                },
                "&.Mui-disabled": {
                  background: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : null
              }
            >
              {loading ? "Processing..." : `Confirm ${decision}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Enhanced Snackbar for errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        TransitionComponent={Fade}
      >
        <Alert
          severity="error"
          onClose={() => setSnackbarOpen(false)}
          sx={{
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            border: `1px solid ${alpha("#f44336", 0.2)}`,
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSnackbarOpen(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PartRequest;
